import defaults from "./locales/defaults.json";
import { loadLanguage } from "./lib/load-language";
import { resolveLanguage, localeFor, directionFor } from "./lib/languages";
import { readPreference } from "./lib/preferences";
import { ui } from "./lib/localization";
import { Component, StrictMode, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { Admin } from "./Admin";
import "highlight.js/styles/github.css";
import "./styles.css";

interface AppErrorBoundaryState {
  failed: boolean;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("NeuronDeck UI error", error, info);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const language = resolveLanguage(document.documentElement.lang);
    return (
      <main className="app-error" role="alert">
        <div className="app-error-card">
          <span className="eyebrow">NeuronDeck</span>
          <h1>{ui(language, "This page needs a refresh")}</h1>
          <p>{ui(language, "The interface hit an unexpected error. Your conversations are still stored in this browser.")}</p>
          <button type="button" onClick={() => window.location.reload()}>
            {ui(language, "Reload")}
          </button>
        </div>
      </main>
    );
  }
}

const root = createRoot(document.getElementById("root")!);
const start = async () => {
  const language = resolveLanguage(readPreference("neurondeck-language"));
  const copy = defaults[language];
  root.render(<main className="app-error" lang={localeFor(language)} dir={directionFor(language)}><div className="app-error-card" role="status"><h1>NeuronDeck</h1><p>{copy.loading}</p></div></main>);
  try { await loadLanguage(language); }
  catch {
    root.render(<main className="app-error" lang={localeFor(language)} dir={directionFor(language)}><div className="app-error-card" role="alert"><h1>NeuronDeck</h1><p>{copy.loadError}</p><button type="button" onClick={() => void start()}>{copy.reload}</button></div></main>);
    return;
  }
  document.documentElement.lang = localeFor(language);
  document.documentElement.dir = directionFor(language);
  root.render(
  <StrictMode>
    <AppErrorBoundary>
      {/^\/admin\/?$/.test(window.location.pathname) ? <Admin /> : <App />}
    </AppErrorBoundary>
  </StrictMode>,
);

};
void start();
